import { HospitalNew } from 'domain/entities/hospital-model-new'
import { SurgeryCenterNew } from 'domain/entities/surgery-center-model-new'
import { GetSurgicalOrderById } from 'domain/usecases/surgical-order/get-surgical-order-by-id'
import { GetAllHealthPlans } from 'domain/usecases/patient/get-all-health-plans'
import { GetHealthSubPlans } from 'domain/usecases/patient/get-health-sub-plans'
import { UpdateSurgicalOrderNew } from 'domain/usecases/surgical-order/update-surgical-order-new'
import { CreateSurgicalOrder } from 'domain/usecases/surgical-order/create-surgical-order'
import { Techniques } from 'common/enum/techniques'
import { InternationData } from 'domain/entities/patient-model'
import { GetPatientByCpfOrName } from 'domain/usecases/patient/get-patient-by-cpf-or-name'
import { MaritalStatus } from 'presentation/utils/default-marital-status'
import { Document } from 'domain/entities/document'
import { FinishSurgicalOrder } from 'domain/usecases/surgical-order/finish-surgical-order'
import { DataSource } from 'common/enum/data-source'
import { GenderNew } from 'common/enum/gender'

export enum OrderStepsPath {
  REGISTRATION = '/pedido/informacoes-cadastrais',
  PROCEDURE = '/pedido/procedimento-opme',
  CLINICAL_HISTORY = '/pedido/historia-clinica',
  REPORTS_AND_DOCUMENTS = '/pedido/laudos-documentos'
}

export const ORDERED_STEPS: OrderStepsPath[] = [
  OrderStepsPath.REGISTRATION,
  OrderStepsPath.PROCEDURE,
  OrderStepsPath.CLINICAL_HISTORY,
  OrderStepsPath.REPORTS_AND_DOCUMENTS
]

export type LocationV2Props = {
  hospital: {
    hospitalId: number
    hospitalName: string
  }
  surgical_center: {
    surgeryCenterId: number
    surgeryCenterName: string
  }
}

export type PatientV2Props = {
  cpf: string
  name: string
  birthDate: string
  gender: GenderNew | string
  maritalStatus: MaritalStatus | undefined
  phone: string
  email: string
  patientId?: number
  isNewPatient: boolean
  internation?: InternationData
  tutor?: {
    cpf?: string
    name?: string
    phone?: string
    email?: string
  }
}

export type HealthInsuranceV2Props = {
  healthInsuranceId?: number
  healthInsuranceCode: number
  healthInsuranceName: string
  healthCard: string
  validThru: string
  healthPlanCode: number
  healthPlanName: string
  healthSubplanCode: number
  healthSubplanName: string
  companyName?: string
}

export type CooperativeV2Props = {
  cooperative: string
}

export type ProcedureV2Props = {
  code: string
  description: string
  quantity: number
  doctor_name: string
  pro_fat_id: string
  surgery_id: number
  is_main: boolean
  is_selected?: boolean
  source?: DataSource
}

export type ProviderV2Props = {
  name: string
  reason?: string
  is_selected?: boolean
  source?: DataSource
  code: string
}

export type OpmeV2Props = {
  description: string
  quantity: number
  code: string
  is_selected?: boolean
  source?: DataSource
  low_acceptance?: boolean
}

export type CidV2Props = {
  code: string
  description: string
  is_main: boolean
}

export type SurgicalOrderV2Props = {
  surgicalOrderId: number
  orderWasLoadedInContext: boolean
  finalizedSurgicalOrderId?: number
  documents: Document[]
  reportRecommendation: string
}

export type SurgicalOrderServiceContract = {
  getSurgicalOrderById: (
    params: GetSurgicalOrderById.Params
  ) => Promise<GetSurgicalOrderById.Model>
  createSurgicalOrder: (
    params: CreateSurgicalOrder.Params
  ) => Promise<CreateSurgicalOrder.Model>
  updateSurgicalOrder: (
    params: UpdateSurgicalOrderNew.Params
  ) => Promise<UpdateSurgicalOrderNew.Model>
  finishSurgicalOrder: (
    params: FinishSurgicalOrder.Params
  ) => Promise<FinishSurgicalOrder.Model>
}

export type PatientServiceContract = {
  getPatientByCpfOrName: (
    params: GetPatientByCpfOrName.Params
  ) => Promise<GetPatientByCpfOrName.Model>
  getHealthInsuranceByPatient: (params: {
    patient_id?: number
    last_health_insurance: boolean
    is_active: boolean
  }) => Promise<
    {
      health_card: string
      health_insurance_name: string
      health_insurance_code: number
      health_plan_name: string
      health_plan_code: number
      health_sub_plan_name: string
      health_sub_plan_code: number
      valid_thru: string
    }[]
  >
  getAllHealthPlans: () => Promise<GetAllHealthPlans.Model>
  getHealthSubPlans: (
    params: GetHealthSubPlans.Params
  ) => Promise<GetHealthSubPlans.Model>
}

export type HospitalServiceContract = {
  loadHospitalsByDoctor: () => Promise<HospitalNew[]>
  loadSurgeryCenters: (params: {
    hospital_id: number
  }) => Promise<SurgeryCenterNew[]>
}

export type RegistrationInfoFormValues = {
  hospitalId: number
  hospitalName: string
  surgeryCenterId: number
  surgeryCenterName: string
  cpf: string
  name: string
  birthDate: string
  gender: GenderNew
  maritalStatus: MaritalStatus
  phone: string
  email: string
  healthInsuranceName: string
  healthInsuranceCode: number
  healthCard: string
  validThru: string
  healthPlanName: string
  healthPlanCode: number
  healthSubplanName: string
  healthSubplanCode: number
  cooperative: string
  tutor: {
    cpf: string
    name: string
    phone: string
    email: string
  }
}

export type ClinicalHistoryFormValues = {
  clinicalHistoryDescription: string
  clinicalHistoryAspects: string
  clinicalHistoryLatexAllergy: string
  clinicalHistoryOtherAllergy: string
  clinicalHistoryOncology: boolean | undefined
  clinicalHistoryBloodTransfusion: boolean | undefined
  clinicalHistoryTechnique: Techniques
  clinicalHistoryCongelation: boolean | undefined
  clinicalHistoryBloodReserve: boolean | undefined
  clinicalHistoryHospitalized: boolean | undefined
  clinicalHistoryExpectedDate: string | null
  clinicalHistoryBullishForecastDate: string | null
  clinicalHistoryHospitalizationType: string
  clinicalHistoryHospitalizationMode: string
  clinicalHistoryHospitalizationDailyQuantity: number
  clinicalHistoryCti: boolean | undefined
  clinicalHistoryMedicalObservations: string
}
